import { makeApi } from "@zodios/core";
import { z } from "zod";

import { validationErrorSchema } from "../shared.ts";
import { moduleResourceSchema } from "./interactModules.ts";

// Create Schema
export const moduleSetSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  tags: z.array(z.string()).optional(),
  moduleIds: z.array(z.string()).optional(),
});

// Update Schema
export const moduleSetUpdateSchema = moduleSetSchema.omit({ moduleIds: true });

// Fetched Schema for responses
export const moduleSetResourceSchema = moduleSetSchema.omit({ moduleIds: true }).extend({
  modules: z.array(moduleResourceSchema),
});

export type InteractModuleSet = z.infer<typeof moduleSetResourceSchema>;
export type UpdateInteractModuleSet = z.infer<typeof moduleSetUpdateSchema>;

export const interactModuleSetsApi = makeApi([
  {
    method: "get",
    path: "/interact-module-sets",
    alias: "getInteractModuleSets",
    parameters: [
      {
        type: "Query",
        name: "owned",
        schema: z.boolean().optional(),
      },
    ],
    response: z.array(moduleSetResourceSchema),
  },
  {
    method: "get",
    path: "/interact-module-sets/:moduleSetId",
    alias: "getInteractModuleSet",
    parameters: [
      {
        type: "Path",
        name: "moduleSetId",
        schema: z.string(),
      },
    ],
    response: moduleSetResourceSchema,
    errors: [
      {
        status: 404,
        schema: z.string(),
      },
    ],
  },
  {
    method: "post",
    path: "/interact-module-sets",
    alias: "createInteractModuleSet",
    parameters: [
      {
        type: "Body",
        name: "moduleSet",
        schema: moduleSetSchema,
      },
    ],
    response: z.void(),
    status: 204,
    errors: [
      {
        status: 404,
        schema: z.string(),
      },
    ],
  },
  {
    method: "post",
    path: "/interact-module-sets/:moduleSetId/modules",
    alias: "addInteractModuleSetModule",
    parameters: [
      {
        type: "Path",
        name: "moduleSetId",
        schema: z.string(),
      },
      {
        type: "Body",
        name: "moduleId",
        schema: z.object({
          moduleId: z.string(),
        }),
      },
    ],
    response: z.void(),
    status: 204,
    errors: [
      {
        status: 404,
        schema: z.string(),
      },
    ],
  },
  {
    method: "put",
    path: "/interact-module-sets/:moduleSetId",
    alias: "updateInteractModuleSet",
    parameters: [
      {
        type: "Path",
        name: "moduleSetId",
        schema: z.string(),
      },
      {
        type: "Body",
        name: "moduleSet",
        schema: moduleSetUpdateSchema,
      },
    ],
    response: z.void(),
    status: 204,
    errors: [
      {
        status: 400,
        schema: validationErrorSchema,
      },
    ],
  },
  {
    method: "delete",
    path: "/interact-module-sets/:moduleSetId",
    alias: "deleteInteractModuleSet",
    parameters: [
      {
        type: "Path",
        name: "moduleSetId",
        schema: z.string(),
      },
    ],
    response: z.void(),
    status: 204,
  },
  {
    method: "delete",
    path: "/interact-module-sets/:moduleSetId/modules/:moduleId",
    alias: "deleteInteractModuleSetModule",
    parameters: [
      {
        type: "Path",
        name: "moduleSetId",
        schema: z.string(),
      },
      {
        type: "Path",
        name: "moduleId",
        schema: z.string(),
      },
    ],
    response: z.void(),
    status: 204,
  },
]);
